import { useEffect, useState } from "react";
import type { Editor } from "@tiptap/react";
import { docTables, readPlotSpec, resolveLinkedPlot, type LinkedPlot, type PlotSpec } from "@/lib/tikzPlot";

/**
 * Sets a figure's source in a transaction kept out of the undo history: Undo
 * then undoes the table edit, and the plot is written again to match.
 */
export function writeSourceOutsideHistory(editor: Pick<Editor, "state" | "view">, pos: number | undefined, source: string, plot?: PlotSpec): void {
  if (typeof pos !== "number" || !editor.view) return;
  const node = editor.state.doc.nodeAt(pos);
  if (!node) return;
  const attrs = { ...node.attrs, source, ...(plot ? { plot } : {}) };
  editor.view.dispatch(editor.state.tr.setNodeMarkup(pos, undefined, attrs).setMeta("addToHistory", false));
}

/**
 * A plot linked to a table follows it (TikZ step 4a): whenever the document
 * changes, the figure's source is written again from its table, and saved like
 * any edit when it differs. Nothing happens for a figure without a plot spec.
 * The table being deleted leaves the source as it was (the last drawing stays).
 */
export function useLinkedPlot(
  editor: Editor | null | undefined,
  plot: unknown,
  source: string,
  /** Writes the source, and the spec when its columns were found again elsewhere. */
  setSource: (source: string, plot?: PlotSpec) => void,
): LinkedPlot | null {
  const spec = readPlotSpec(plot);
  const specKey = spec ? JSON.stringify(spec) : "";
  const [linked, setLinked] = useState<LinkedPlot | null>(null);

  useEffect(() => {
    if (!spec || !editor) {
      setLinked(null);
      return;
    }
    // Recompute only when the linked table's node changed (nodes are immutable).
    let lastAttrs: unknown = undefined;
    let lastNames = "";
    const refresh = () => {
      const tables = docTables(editor.state?.doc);
      const table = tables.find((t) => t.blockId === spec.table);
      const names = tables.map((t) => `${t.blockId}:${t.attrs.caption ? 1 : 0}`).join(",");
      if (table?.attrs === lastAttrs && names === lastNames && lastAttrs !== undefined) return;
      lastAttrs = table?.attrs ?? null;
      lastNames = names;
      setLinked(resolveLinkedPlot(tables, spec));
    };
    refresh();
    editor.on("update", refresh);
    return () => {
      editor.off("update", refresh);
    };
    // specKey stands for spec.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, specKey]);

  useEffect(() => {
    if (linked?.state !== "linked") return;
    const specChanged = JSON.stringify(linked.spec) !== specKey;
    if (linked.source !== source || specChanged) setSource(linked.source, specChanged ? linked.spec : undefined);
  }, [linked, source, setSource, specKey]);

  return linked;
}
